export type LanguageCode = 'it' | 'en' | 'fr' | 'de';

export type TranslationKey =
    | 'common.actions'
    | 'common.previous'
    | 'common.next'
    | 'common.page'
    | 'common.pageNotFound'
    | 'group.events'
    | 'group.reports'
    | 'group.users'
    | 'group.settings'
    | 'group.teams'
    | 'navbar.connectedTo'
    | 'navbar.athletes'
    | 'navbar.administration'
    | 'navbar.changeAssociation'
    | 'navbar.logout'
    | 'settings.profile'
    | 'settings.name'
    | 'settings.email'
    | 'settings.association'
    | 'settings.role'
    | 'settings.logout'
    | 'settings.sessions'
    | 'settings.sessionId'
    | 'settings.ip'
    | 'settings.current'
    | 'settings.yes'
    | 'settings.no'
    | 'settings.revokeSession'
    | 'settings.revoke'
    | 'settings.sessionRevokedTitle'
    | 'settings.sessionRevokedDescription'
    | 'settings.language'
    | 'settings.languageDescription'
    | 'settings.languageItalian'
    | 'settings.languageEnglish'
    | 'settings.languageFrench'
    | 'settings.languageGerman'
    | 'route.dashboard'
    | 'route.devInfo'
    | 'route.forbidden'
    | 'route.notFound'
    | 'route.serverError'
    | 'route.eventCalendar'
    | 'route.createEvent'
    | 'route.viewEvent'
    | 'route.createReport'
    | 'route.reports'
    | 'route.eventReports'
    | 'route.reportDetail'
    | 'route.settings'
    | 'route.userOverview'
    | 'route.viewInvitation'
    | 'route.addUser'
    | 'route.reportTypes'
    | 'route.associationSettings'
    | 'route.editReportType'
    | 'route.teams'
    | 'route.editTeam'
    | 'dashboard.welcome'
    | 'dashboard.pendingReportsCurrentEvents'
    | 'dashboard.savedDraft'
    | 'dashboard.notCompiled'
    | 'dashboard.savedDraftDescription'
    | 'dashboard.notCompiledDescription'
    | 'dashboard.openDraft'
    | 'dashboard.compileNow'
    | 'dashboard.noRecentPending'
    | 'dashboard.openReports'
    | 'dashboard.currentEvents'
    | 'dashboard.pastEvents'
    | 'dashboard.openReportsOverview'
    | 'reportCreate.invalidEventTitle'
    | 'reportCreate.invalidEventDescription'
    | 'reportCreate.eventLabel'
    | 'reportCreate.reportDateLabel'
    | 'reportCreate.startPrompt'
    | 'reportCreate.starting'
    | 'reportCreate.startYes'
    | 'reportCreate.cancel'
    | 'reportCreate.loadedFromUrl'
    | 'reportCreate.newReportCreated'
    | 'reportCreate.fillFields'
    | 'reportCreate.save'
    | 'reportCreate.send'
    | 'reportCreate.noFieldsConfigured'
    | 'reportCreate.outsideEventWindow'
    | 'reportCreate.backToEvent'
    | 'reportCreate.errorTitle'
    | 'reportCreate.draftIdMissing'
    | 'reportCreate.savedTitle'
    | 'reportCreate.savedDescription'
    | 'reportCreate.submittedTitle'
    | 'reportCreate.submittedDescription'
    | 'reportCreate.validationTitle'
    | 'reportCreate.reportTypeRequired';

type TranslationMap = Record<TranslationKey, string>;

export const translations: Record<LanguageCode, TranslationMap> = {
    en: {
        'common.actions': 'Actions',
        'common.previous': 'Previous',
        'common.next': 'Next',
        'common.page': 'Page',
        'common.pageNotFound': 'Page not found',
        'group.events': 'Events',
        'group.reports': 'Reports',
        'group.users': 'Users',
        'group.settings': 'Settings',
        'group.teams': 'Teams',
        'navbar.connectedTo': 'Connected to',
        'navbar.athletes': 'Athletes',
        'navbar.administration': 'Administration',
        'navbar.changeAssociation': 'Change association',
        'navbar.logout': 'Logout',
        'settings.profile': 'Profile',
        'settings.name': 'Name',
        'settings.email': 'Email',
        'settings.association': 'Association',
        'settings.role': 'Role',
        'settings.logout': 'Logout',
        'settings.sessions': 'Sessions',
        'settings.sessionId': 'Session ID',
        'settings.ip': 'IP',
        'settings.current': 'Current',
        'settings.yes': 'Yes',
        'settings.no': 'No',
        'settings.revokeSession': 'Revoke session',
        'settings.revoke': 'Revoke',
        'settings.sessionRevokedTitle': 'Session revoked',
        'settings.sessionRevokedDescription': 'Session closed successfully',
        'settings.language': 'Language',
        'settings.languageDescription': 'Choose the dashboard language.',
        'settings.languageItalian': 'Italian',
        'settings.languageEnglish': 'English',
        'settings.languageFrench': 'French',
        'settings.languageGerman': 'German',
        'route.dashboard': 'Dashboard',
        'route.devInfo': 'Developer Info',
        'route.forbidden': 'Forbidden',
        'route.notFound': 'Page Not Found',
        'route.serverError': 'Server Error',
        'route.eventCalendar': 'Event Calendar',
        'route.createEvent': 'Create Event',
        'route.viewEvent': 'View Event',
        'route.createReport': 'Create Report',
        'route.reports': 'Reports',
        'route.eventReports': 'Event reports',
        'route.reportDetail': 'Report detail',
        'route.settings': 'Settings',
        'route.userOverview': 'User Overview',
        'route.viewInvitation': 'View Invitation',
        'route.addUser': 'Add User',
        'route.reportTypes': 'Report Types',
        'route.associationSettings': 'Association Settings',
        'route.editReportType': 'Edit Report Type',
        'route.teams': 'Teams',
        'route.editTeam': 'Edit Team',
        'dashboard.welcome': 'Welcome',
        'dashboard.pendingReportsCurrentEvents': 'You have {count} pending reports in current events.',
        'dashboard.savedDraft': 'Draft - {day}',
        'dashboard.notCompiled': 'Not compiled - {day}',
        'dashboard.savedDraftDescription': 'Saved draft report. You can reopen, edit, and submit it.',
        'dashboard.notCompiledDescription': 'No report has been compiled for this day yet.',
        'dashboard.openDraft': 'Open draft',
        'dashboard.compileNow': 'Compile now',
        'dashboard.noRecentPending': 'No report drafts or pending report days in the last month.',
        'dashboard.openReports': 'Open reports',
        'dashboard.currentEvents': 'Current events: {count}',
        'dashboard.pastEvents': 'Past events: {count}',
        'dashboard.openReportsOverview': 'Open reports overview',
        'reportCreate.invalidEventTitle': 'Invalid event',
        'reportCreate.invalidEventDescription': 'Missing event id',
        'reportCreate.eventLabel': 'Event',
        'reportCreate.reportDateLabel': 'Report date',
        'reportCreate.startPrompt': 'Do you want to start your report?',
        'reportCreate.starting': 'Creating...',
        'reportCreate.startYes': 'Yes, start',
        'reportCreate.cancel': 'Cancel',
        'reportCreate.loadedFromUrl': 'Loaded report from URL parameter.',
        'reportCreate.newReportCreated': 'New report created.',
        'reportCreate.fillFields': 'Fill report fields',
        'reportCreate.save': 'Save',
        'reportCreate.send': 'Send',
        'reportCreate.noFieldsConfigured': 'This report type has no fields configured.',
        'reportCreate.outsideEventWindow': 'You can create a report only during the event days.',
        'reportCreate.backToEvent': 'Back to event',
        'reportCreate.errorTitle': 'Error',
        'reportCreate.draftIdMissing': 'Draft id not returned by backend',
        'reportCreate.savedTitle': 'Saved',
        'reportCreate.savedDescription': 'Draft saved successfully',
        'reportCreate.submittedTitle': 'Submitted',
        'reportCreate.submittedDescription': 'Report submitted successfully',
        'reportCreate.validationTitle': 'Validation error',
        'reportCreate.reportTypeRequired': 'Report type is required',
    },
    it: {
        'common.actions': 'Azioni',
        'common.previous': 'Precedente',
        'common.next': 'Successivo',
        'common.page': 'Pagina',
        'common.pageNotFound': 'Pagina non trovata',
        'group.events': 'Eventi',
        'group.reports': 'Report',
        'group.users': 'Utenti',
        'group.settings': 'Impostazioni',
        'group.teams': 'Squadre',
        'navbar.connectedTo': 'Connesso a',
        'navbar.athletes': 'Atleti',
        'navbar.administration': 'Amministrazione',
        'navbar.changeAssociation': 'Cambia società',
        'navbar.logout': 'Esci',
        'settings.profile': 'Profilo',
        'settings.name': 'Nome',
        'settings.email': 'Email',
        'settings.association': 'Società',
        'settings.role': 'Ruolo',
        'settings.logout': 'Esci',
        'settings.sessions': 'Sessioni',
        'settings.sessionId': 'ID Sessione',
        'settings.ip': 'IP',
        'settings.current': 'Corrente',
        'settings.yes': 'Sì',
        'settings.no': 'No',
        'settings.revokeSession': 'Revoca sessione',
        'settings.revoke': 'Revoca',
        'settings.sessionRevokedTitle': 'Sessione revocata',
        'settings.sessionRevokedDescription': 'Sessione chiusa con successo',
        'settings.language': 'Lingua',
        'settings.languageDescription': 'Scegli la lingua della dashboard.',
        'settings.languageItalian': 'Italiano',
        'settings.languageEnglish': 'Inglese',
        'settings.languageFrench': 'Francese',
        'settings.languageGerman': 'Tedesco',
        'route.dashboard': 'Dashboard',
        'route.devInfo': 'Info sviluppatore',
        'route.forbidden': 'Accesso negato',
        'route.notFound': 'Pagina non trovata',
        'route.serverError': 'Errore server',
        'route.eventCalendar': 'Calendario eventi',
        'route.createEvent': 'Crea evento',
        'route.viewEvent': 'Visualizza evento',
        'route.createReport': 'Crea report',
        'route.reports': 'Report',
        'route.eventReports': 'Report evento',
        'route.reportDetail': 'Dettaglio report',
        'route.settings': 'Impostazioni',
        'route.userOverview': 'Panoramica utenti',
        'route.viewInvitation': 'Visualizza inviti',
        'route.addUser': 'Aggiungi utente',
        'route.reportTypes': 'Tipi report',
        'route.associationSettings': 'Impostazioni società',
        'route.editReportType': 'Modifica tipo report',
        'route.teams': 'Squadre',
        'route.editTeam': 'Modifica squadra',
        'dashboard.welcome': 'Benvenuto',
        'dashboard.pendingReportsCurrentEvents': 'Hai {count} report in sospeso negli eventi correnti.',
        'dashboard.savedDraft': 'Bozza - {day}',
        'dashboard.notCompiled': 'Non compilato - {day}',
        'dashboard.savedDraftDescription': 'Bozza salvata. Puoi riaprire, modificare e inviare.',
        'dashboard.notCompiledDescription': 'Nessun report è stato compilato per questo giorno.',
        'dashboard.openDraft': 'Apri bozza',
        'dashboard.compileNow': 'Compila ora',
        'dashboard.noRecentPending': 'Nessuna bozza o giorno non compilato nell’ultimo mese.',
        'dashboard.openReports': 'Apri report',
        'dashboard.currentEvents': 'Eventi correnti: {count}',
        'dashboard.pastEvents': 'Eventi passati: {count}',
        'dashboard.openReportsOverview': 'Apri panoramica report',
        'reportCreate.invalidEventTitle': 'Evento non valido',
        'reportCreate.invalidEventDescription': 'ID evento mancante',
        'reportCreate.eventLabel': 'Evento',
        'reportCreate.reportDateLabel': 'Data report',
        'reportCreate.startPrompt': 'Vuoi iniziare il tuo report?',
        'reportCreate.starting': 'Creazione...',
        'reportCreate.startYes': 'Sì, inizia',
        'reportCreate.cancel': 'Annulla',
        'reportCreate.loadedFromUrl': 'Report caricato dal parametro URL.',
        'reportCreate.newReportCreated': 'Nuovo report creato.',
        'reportCreate.fillFields': 'Compila i campi del report',
        'reportCreate.save': 'Salva',
        'reportCreate.send': 'Invia',
        'reportCreate.noFieldsConfigured': 'Questo tipo di report non ha campi configurati.',
        'reportCreate.outsideEventWindow': 'Puoi creare un report solo durante i giorni dell’evento.',
        'reportCreate.backToEvent': 'Torna all’evento',
        'reportCreate.errorTitle': 'Errore',
        'reportCreate.draftIdMissing': 'ID bozza non restituito dal backend',
        'reportCreate.savedTitle': 'Salvato',
        'reportCreate.savedDescription': 'Bozza salvata con successo',
        'reportCreate.submittedTitle': 'Inviato',
        'reportCreate.submittedDescription': 'Report inviato con successo',
        'reportCreate.validationTitle': 'Errore di validazione',
        'reportCreate.reportTypeRequired': 'Il tipo di report è obbligatorio',
    },
    fr: {
        'common.actions': 'Actions',
        'common.previous': 'Précédent',
        'common.next': 'Suivant',
        'common.page': 'Page',
        'common.pageNotFound': 'Page non trouvée',
        'group.events': 'Événements',
        'group.reports': 'Rapports',
        'group.users': 'Utilisateurs',
        'group.settings': 'Paramètres',
        'group.teams': 'Équipes',
        'navbar.connectedTo': 'Connecté à',
        'navbar.athletes': 'Athlètes',
        'navbar.administration': 'Administration',
        'navbar.changeAssociation': "Changer d'association",
        'navbar.logout': 'Déconnexion',
        'settings.profile': 'Profil',
        'settings.name': 'Nom',
        'settings.email': 'Email',
        'settings.association': 'Association',
        'settings.role': 'Rôle',
        'settings.logout': 'Déconnexion',
        'settings.sessions': 'Sessions',
        'settings.sessionId': 'ID Session',
        'settings.ip': 'IP',
        'settings.current': 'Actuelle',
        'settings.yes': 'Oui',
        'settings.no': 'Non',
        'settings.revokeSession': 'Révoquer la session',
        'settings.revoke': 'Révoquer',
        'settings.sessionRevokedTitle': 'Session révoquée',
        'settings.sessionRevokedDescription': 'Session fermée avec succès',
        'settings.language': 'Langue',
        'settings.languageDescription': 'Choisissez la langue du tableau de bord.',
        'settings.languageItalian': 'Italien',
        'settings.languageEnglish': 'Anglais',
        'settings.languageFrench': 'Français',
        'settings.languageGerman': 'Allemand',
        'route.dashboard': 'Tableau de bord',
        'route.devInfo': 'Info développeur',
        'route.forbidden': 'Accès interdit',
        'route.notFound': 'Page non trouvée',
        'route.serverError': 'Erreur serveur',
        'route.eventCalendar': "Calendrier d'événements",
        'route.createEvent': 'Créer un événement',
        'route.viewEvent': "Voir l'événement",
        'route.createReport': 'Créer un rapport',
        'route.reports': 'Rapports',
        'route.eventReports': "Rapports d'événement",
        'route.reportDetail': 'Détail du rapport',
        'route.settings': 'Paramètres',
        'route.userOverview': 'Vue utilisateurs',
        'route.viewInvitation': 'Voir les invitations',
        'route.addUser': 'Ajouter un utilisateur',
        'route.reportTypes': 'Types de rapport',
        'route.associationSettings': "Paramètres de l'association",
        'route.editReportType': 'Modifier le type de rapport',
        'route.teams': 'Équipes',
        'route.editTeam': "Modifier l'équipe",
        'dashboard.welcome': 'Bienvenue',
        'dashboard.pendingReportsCurrentEvents': 'Vous avez {count} rapports en attente dans les événements en cours.',
        'dashboard.savedDraft': 'Brouillon - {day}',
        'dashboard.notCompiled': 'Non compilé - {day}',
        'dashboard.savedDraftDescription': 'Brouillon enregistré. Vous pouvez rouvrir, modifier et envoyer.',
        'dashboard.notCompiledDescription': 'Aucun rapport n’a été compilé pour ce jour.',
        'dashboard.openDraft': 'Ouvrir le brouillon',
        'dashboard.compileNow': 'Compiler maintenant',
        'dashboard.noRecentPending': 'Aucun brouillon ou jour en attente au cours du dernier mois.',
        'dashboard.openReports': 'Ouvrir les rapports',
        'dashboard.currentEvents': 'Événements en cours : {count}',
        'dashboard.pastEvents': 'Événements passés : {count}',
        'dashboard.openReportsOverview': 'Ouvrir la vue des rapports',
        'reportCreate.invalidEventTitle': 'Événement invalide',
        'reportCreate.invalidEventDescription': "ID d'événement manquant",
        'reportCreate.eventLabel': 'Événement',
        'reportCreate.reportDateLabel': 'Date du rapport',
        'reportCreate.startPrompt': 'Voulez-vous commencer votre rapport ?',
        'reportCreate.starting': 'Création...',
        'reportCreate.startYes': 'Oui, commencer',
        'reportCreate.cancel': 'Annuler',
        'reportCreate.loadedFromUrl': 'Rapport chargé depuis le paramètre URL.',
        'reportCreate.newReportCreated': 'Nouveau rapport créé.',
        'reportCreate.fillFields': 'Remplir les champs du rapport',
        'reportCreate.save': 'Enregistrer',
        'reportCreate.send': 'Envoyer',
        'reportCreate.noFieldsConfigured': "Ce type de rapport n'a pas de champs configurés.",
        'reportCreate.outsideEventWindow': 'Vous pouvez créer un rapport uniquement pendant les jours de l’événement.',
        'reportCreate.backToEvent': "Retour à l'événement",
        'reportCreate.errorTitle': 'Erreur',
        'reportCreate.draftIdMissing': 'ID du brouillon non renvoyé par le backend',
        'reportCreate.savedTitle': 'Enregistré',
        'reportCreate.savedDescription': 'Brouillon enregistré avec succès',
        'reportCreate.submittedTitle': 'Envoyé',
        'reportCreate.submittedDescription': 'Rapport envoyé avec succès',
        'reportCreate.validationTitle': 'Erreur de validation',
        'reportCreate.reportTypeRequired': 'Le type de rapport est requis',
    },
    de: {
        'common.actions': 'Aktionen',
        'common.previous': 'Zurück',
        'common.next': 'Weiter',
        'common.page': 'Seite',
        'common.pageNotFound': 'Seite nicht gefunden',
        'group.events': 'Events',
        'group.reports': 'Berichte',
        'group.users': 'Benutzer',
        'group.settings': 'Einstellungen',
        'group.teams': 'Teams',
        'navbar.connectedTo': 'Verbunden mit',
        'navbar.athletes': 'Athleten',
        'navbar.administration': 'Verwaltung',
        'navbar.changeAssociation': 'Verein wechseln',
        'navbar.logout': 'Abmelden',
        'settings.profile': 'Profil',
        'settings.name': 'Name',
        'settings.email': 'E-Mail',
        'settings.association': 'Verein',
        'settings.role': 'Rolle',
        'settings.logout': 'Abmelden',
        'settings.sessions': 'Sitzungen',
        'settings.sessionId': 'Sitzungs-ID',
        'settings.ip': 'IP',
        'settings.current': 'Aktuell',
        'settings.yes': 'Ja',
        'settings.no': 'Nein',
        'settings.revokeSession': 'Sitzung beenden',
        'settings.revoke': 'Beenden',
        'settings.sessionRevokedTitle': 'Sitzung beendet',
        'settings.sessionRevokedDescription': 'Sitzung erfolgreich geschlossen',
        'settings.language': 'Sprache',
        'settings.languageDescription': 'Wähle die Sprache des Dashboards.',
        'settings.languageItalian': 'Italienisch',
        'settings.languageEnglish': 'Englisch',
        'settings.languageFrench': 'Französisch',
        'settings.languageGerman': 'Deutsch',
        'route.dashboard': 'Dashboard',
        'route.devInfo': 'Entwicklerinfo',
        'route.forbidden': 'Zugriff verweigert',
        'route.notFound': 'Seite nicht gefunden',
        'route.serverError': 'Serverfehler',
        'route.eventCalendar': 'Eventkalender',
        'route.createEvent': 'Event erstellen',
        'route.viewEvent': 'Event anzeigen',
        'route.createReport': 'Bericht erstellen',
        'route.reports': 'Berichte',
        'route.eventReports': 'Event-Berichte',
        'route.reportDetail': 'Berichtdetails',
        'route.settings': 'Einstellungen',
        'route.userOverview': 'Benutzerübersicht',
        'route.viewInvitation': 'Einladungen anzeigen',
        'route.addUser': 'Benutzer hinzufügen',
        'route.reportTypes': 'Berichtstypen',
        'route.associationSettings': 'Vereinseinstellungen',
        'route.editReportType': 'Berichtstyp bearbeiten',
        'route.teams': 'Teams',
        'route.editTeam': 'Team bearbeiten',
        'dashboard.welcome': 'Willkommen',
        'dashboard.pendingReportsCurrentEvents': 'Du hast {count} ausstehende Berichte in aktuellen Events.',
        'dashboard.savedDraft': 'Entwurf - {day}',
        'dashboard.notCompiled': 'Nicht ausgefüllt - {day}',
        'dashboard.savedDraftDescription': 'Entwurf gespeichert. Du kannst ihn wieder öffnen, bearbeiten und senden.',
        'dashboard.notCompiledDescription': 'Für diesen Tag wurde noch kein Bericht ausgefüllt.',
        'dashboard.openDraft': 'Entwurf öffnen',
        'dashboard.compileNow': 'Jetzt ausfüllen',
        'dashboard.noRecentPending': 'Keine Entwürfe oder ausstehenden Berichtstage im letzten Monat.',
        'dashboard.openReports': 'Berichte öffnen',
        'dashboard.currentEvents': 'Aktuelle Events: {count}',
        'dashboard.pastEvents': 'Vergangene Events: {count}',
        'dashboard.openReportsOverview': 'Berichtsübersicht öffnen',
        'reportCreate.invalidEventTitle': 'Ungültiges Event',
        'reportCreate.invalidEventDescription': 'Fehlende Event-ID',
        'reportCreate.eventLabel': 'Event',
        'reportCreate.reportDateLabel': 'Berichtsdatum',
        'reportCreate.startPrompt': 'Möchtest du deinen Bericht starten?',
        'reportCreate.starting': 'Erstellung...',
        'reportCreate.startYes': 'Ja, starten',
        'reportCreate.cancel': 'Abbrechen',
        'reportCreate.loadedFromUrl': 'Bericht aus URL-Parameter geladen.',
        'reportCreate.newReportCreated': 'Neuer Bericht erstellt.',
        'reportCreate.fillFields': 'Berichtsfelder ausfüllen',
        'reportCreate.save': 'Speichern',
        'reportCreate.send': 'Senden',
        'reportCreate.noFieldsConfigured': 'Dieser Berichtstyp hat keine konfigurierten Felder.',
        'reportCreate.outsideEventWindow': 'Du kannst nur an den Event-Tagen einen Bericht erstellen.',
        'reportCreate.backToEvent': 'Zurück zum Event',
        'reportCreate.errorTitle': 'Fehler',
        'reportCreate.draftIdMissing': 'Entwurfs-ID wurde vom Backend nicht zurückgegeben',
        'reportCreate.savedTitle': 'Gespeichert',
        'reportCreate.savedDescription': 'Entwurf erfolgreich gespeichert',
        'reportCreate.submittedTitle': 'Gesendet',
        'reportCreate.submittedDescription': 'Bericht erfolgreich gesendet',
        'reportCreate.validationTitle': 'Validierungsfehler',
        'reportCreate.reportTypeRequired': 'Berichtstyp ist erforderlich',
    },
};

export const assertTranslationConsistency = () => {
    const baseKeys = Object.keys(translations.it).sort();
    (Object.keys(translations) as LanguageCode[]).forEach((language) => {
        const currentKeys = Object.keys(translations[language]).sort();
        if (baseKeys.length !== currentKeys.length) {
            throw new Error(`Translation key count mismatch for language: ${language}`);
        }
        baseKeys.forEach((key, index) => {
            if (key !== currentKeys[index]) {
                throw new Error(`Missing translation key "${key}" in language: ${language}`);
            }
        });
    });
};
